import type { PlayerRow, WindowName } from '@/api/types';
import { toPlayerId } from '@/lib/player-id';

export type JoinedFilter = '' | 'joined' | 'notJoined';

// '' means "not filtered" for every field, so a select's first option can clear it.
export interface PlayerFilters {
  search: string;
  agent: string;
  joined: JoinedFilter;
  depositedIn: WindowName | '';
  activeIn: WindowName | '';
  country: string;
  currency: string;
  hideTest: boolean;
}

export const NO_FILTERS: PlayerFilters = {
  search: '',
  agent: '',
  joined: '',
  depositedIn: '',
  activeIn: '',
  country: '',
  currency: '',
  hideTest: false,
};

export function hasFilters(filters: PlayerFilters): boolean {
  return (Object.keys(NO_FILTERS) as (keyof PlayerFilters)[]).some((field) => filters[field] !== NO_FILTERS[field]);
}

function matchesSearch(player: PlayerRow, needle: string): boolean {
  if (!needle) return true;
  const haystack = [
    player.playerUuid,
    toPlayerId(player.playerUuid),
    player.profile?.username,
    player.profile?.email,
    player.profile?.firstName,
    player.profile?.lastName,
    ...player.agentIds,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

function matchesJoined(player: PlayerRow, joined: JoinedFilter): boolean {
  if (joined === 'joined') return player.redeemedAt !== null;
  if (joined === 'notJoined') return player.redeemedAt === null;
  return true;
}

// A player who never joined has no windows, so a window filter always leaves them out.
export function filterPlayers(players: PlayerRow[], filters: PlayerFilters): PlayerRow[] {
  const needle = filters.search.trim().toLowerCase();
  return players.filter(
    (player) =>
      (!filters.hideTest || !player.isTest) &&
      matchesSearch(player, needle) &&
      (!filters.agent || player.agentIds.includes(filters.agent)) &&
      matchesJoined(player, filters.joined) &&
      (!filters.depositedIn || player.windows?.[filters.depositedIn].deposited === true) &&
      (!filters.activeIn || player.windows?.[filters.activeIn].active === true) &&
      (!filters.country || player.profile?.country === filters.country) &&
      (!filters.currency || player.profile?.currency === filters.currency),
  );
}

function distinct(values: (string | null | undefined)[]): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].sort((a, b) => a.localeCompare(b));
}

export interface PlayerFilterOptions {
  agents: string[];
  countries: string[];
  currencies: string[];
}

export function filterOptions(players: PlayerRow[]): PlayerFilterOptions {
  return {
    agents: distinct(players.flatMap((player) => player.agentIds)),
    countries: distinct(players.map((player) => player.profile?.country)),
    currencies: distinct(players.map((player) => player.profile?.currency)),
  };
}
